// Движок клиентской маршрутизации (без axios/window - работает и в основном потоке,
// и в Web Worker, см. routingWorkerCore.ts). Тайлы графа (GET /routing/graph по сетке 0.05 x 0.03°)
// - память -> IndexedDB (stale-while-revalidate, ETag = версия графа) -> сеть;
// нужные отрезку тайлы склеиваются в один граф (регион) для utils/clientRouter.ts.
// routeClient возвращает null, когда тайлов получить неоткуда (нет сети и нет
// сохранённых) - вызывающий рисует обычный fallback; на сервер не обращаемся.
import { MAX_LEG_M, routeOnGraph, straight } from "./clientRouter";
import { buildGraphIndex, decodeGraphChunk, mergeChunks, type GraphChunk, type GraphIndex } from "./routingGraph";
import { createIdbTileStore, type TileStore } from "./routingTileStore";
import { tileBBox, tileKey, tilesAround, tilesForLeg, type TileIndex } from "./routingTiles";
import type { RoutingStat } from "./routingStats";
import { haversineDistanceM } from "./routeLineStyle";
import type { Coordinate, RoutedPath } from "../composables/useRoutingPath";

const MAX_MEM_TILES = 80;
const MAX_REGIONS = 4;

interface MemTile {
  chunk: GraphChunk;
  etag: string;
  revalidated: boolean;
}
interface Region {
  keys: Set<string>;
  index: GraphIndex;
}
type Origin = "memory" | "store" | "network";

let store: TileStore = createIdbTileStore();
let mem = new Map<string, MemTile>();
let regions: Region[] = [];
let inflight = new Map<string, Promise<{ tile: MemTile; origin: Origin }>>();
let epoch = 0; // растёт при сбросе - устаревшие ответы не пишутся в кэш
let tileGen = 0; // растёт, когда тайл заменён новой версией графа - регион, собранный раньше, не кэшируем

export function setTileStore(s: TileStore): void {
  store = s;
}

export function clearClientRoutingCache(): void {
  mem = new Map();
  regions = [];
  inflight = new Map();
  epoch++;
}

const putMem = (key: string, tile: MemTile) => {
  mem.delete(key);
  mem.set(key, tile);
  while (mem.size > MAX_MEM_TILES) mem.delete(mem.keys().next().value as string);
};

// Как получить тайл (HTTP GET /routing/graph с bbox тайла и, при перепроверке,
// If-None-Match): в основном потоке - через axios, в воркере - через fetch.
export interface TileRequest {
  params: { min_lon: number; min_lat: number; max_lon: number; max_lat: number };
  headers?: Record<string, string>;
}
export type TileFetcher = (req: TileRequest) => Promise<{ status: number; data: ArrayBuffer; etag: string }>;
let fetcher: TileFetcher | null = null;
export function setTileFetcher(f: TileFetcher): void {
  fetcher = f;
}

const fetchTile = async (ix: number, iy: number, etag?: string) => {
  if (!fetcher) throw new Error("tile fetcher is not set");
  const [minLon, minLat, maxLon, maxLat] = tileBBox(ix, iy);
  return fetcher({
    params: { min_lon: minLon, min_lat: minLat, max_lon: maxLon, max_lat: maxLat },
    headers: etag ? { "If-None-Match": etag } : undefined,
  });
};

// Фоновая перепроверка тайла из хранилища/памяти: 304 - всё как есть; 200 -
// граф обновился: заменяем тайл и сбрасываем склеенные регионы.
const revalidate = (ix: number, iy: number, key: string, etag: string, myEpoch: number) => {
  void (async () => {
    try {
      const r = await fetchTile(ix, iy, etag || undefined);
      if (r.status === 304 || myEpoch !== epoch) return;
      const chunk = decodeGraphChunk(r.data, tileBBox(ix, iy));
      putMem(key, { chunk, etag: r.etag, revalidated: true });
      tileGen++;
      regions = [];
      await store.put({ key, etag: r.etag, buf: r.data, savedAt: Date.now() });
    } catch {
      // оффлайн/ошибка перепроверки - остаёмся на сохранённом тайле
    }
  })();
};

const loadTile = ([ix, iy]: TileIndex): Promise<{ tile: MemTile; origin: Origin }> => {
  const key = tileKey(ix, iy);
  const hit = mem.get(key);
  if (hit) {
    if (!hit.revalidated) {
      hit.revalidated = true;
      revalidate(ix, iy, key, hit.etag, epoch);
    }
    return Promise.resolve({ tile: hit, origin: "memory" });
  }
  let p = inflight.get(key);
  if (!p) {
    const myEpoch = epoch;
    p = (async () => {
      try {
        const stored = await store.get(key);
        if (stored) {
          const tile: MemTile = { chunk: decodeGraphChunk(stored.buf, tileBBox(ix, iy)), etag: stored.etag, revalidated: true };
          if (myEpoch === epoch) putMem(key, tile);
          revalidate(ix, iy, key, stored.etag, myEpoch);
          return { tile, origin: "store" as Origin };
        }
        const r = await fetchTile(ix, iy);
        if (r.status !== 200) throw new Error("unexpected 304");
        const tile: MemTile = { chunk: decodeGraphChunk(r.data, tileBBox(ix, iy)), etag: r.etag, revalidated: true };
        if (myEpoch === epoch) putMem(key, tile);
        await store.put({ key, etag: r.etag, buf: r.data, savedAt: Date.now() });
        return { tile, origin: "network" as Origin };
      } finally {
        inflight.delete(key);
      }
    })();
    inflight.set(key, p);
  }
  return p;
};

// Подгрузить тайлы вокруг точки (центр карты при открытии редактора/линейки и
// после сдвига карты), чтобы первый отрезок не ждал сеть. Ошибки глотаем.
export async function prefetchRoutingAround(lon: number, lat: number, ring = 1): Promise<void> {
  const need = tilesAround(lon, lat, ring).filter(([x, y]) => !mem.has(tileKey(x, y)));
  await Promise.all(need.map((t) => loadTile(t).catch(() => null)));
}

// Фоновый рост региона: тайлы 5x5 вокруг точки + склеенный граф заранее, чтобы
// следующий отрезок не ждал ни сети, ни сборки графа. Ошибки глотаем.
let warmAheadEnabled = true;
export function setWarmAhead(on: boolean): void {
  warmAheadEnabled = on;
}
const warming = new Set<string>();

export async function warmRoutingAround(lon: number, lat: number): Promise<void> {
  const tiles = tilesAround(lon, lat, 2);
  const keys = tiles.map(([x, y]) => tileKey(x, y));
  const covers = () => regions.some((r) => keys.every((k) => r.keys.has(k)));
  const id = keys[12]; // центральный тайл
  if (covers() || warming.has(id)) return;
  warming.add(id);
  const gen = tileGen;
  try {
    const loaded = await Promise.all(tiles.map(loadTile));
    await new Promise((r) => setTimeout(r, 0)); // отдать страницу: сборка графа тяжёлая
    if (gen !== tileGen || covers()) return;
    const region: Region = { keys: new Set(keys), index: buildGraphIndex(mergeChunks(loaded.map((l) => l.tile.chunk))) };
    if (gen === tileGen) regions = [region, ...regions].slice(0, MAX_REGIONS);
  } catch {
    // нет сети/тайлов - обойдёмся обычной загрузкой по требованию
  } finally {
    warming.delete(id);
  }
}

export async function routeClient(
  start: Coordinate,
  destination: Coordinate,
  opts: { destinationMode?: "network" | "poi"; snapRadiusM?: number } = {}
): Promise<{ result: RoutedPath; stat: RoutingStat } | null> {
  const t0 = performance.now();
  const req = { start, destination, destinationMode: opts.destinationMode, snapRadiusM: opts.snapRadiusM };
  const directM = haversineDistanceM(start[1], start[0], destination[1], destination[0]);
  const done = (result: RoutedPath, extra: Partial<RoutingStat> = {}) => ({
    result,
    stat: { mode: "client" as const, ms: performance.now() - t0, status: result.status, ...extra },
  });
  if (directM > MAX_LEG_M) return done(straight("too_long", req));

  // Обход до 10x прямой уходит от неё далеко: коридор растёт вместе с отрезком (до 10 км).
  const tiles = tilesForLeg(start, destination, Math.min(10_000, Math.max(3000, directM * 2.5)));
  if (!tiles) return done(straight("too_long", req));
  const keys = tiles.map(([x, y]) => tileKey(x, y));

  let fetchMs = 0, indexMs = 0, fetchedChunk = false, storeTiles = 0;
  let region = regions.find((r) => keys.every((k) => r.keys.has(k)));
  if (!region) {
    const gen = tileGen;
    const f0 = performance.now();
    let loaded: { tile: MemTile; origin: Origin }[];
    try {
      loaded = await Promise.all(tiles.map(loadTile));
    } catch {
      return null;
    }
    fetchMs = performance.now() - f0;
    fetchedChunk = loaded.some((l) => l.origin === "network");
    storeTiles = loaded.filter((l) => l.origin === "store").length;
    const i0 = performance.now();
    region = { keys: new Set(keys), index: buildGraphIndex(mergeChunks(loaded.map((l) => l.tile.chunk))) };
    indexMs = performance.now() - i0;
    if (gen === tileGen) regions = [region, ...regions].slice(0, MAX_REGIONS);
  }
  const r0 = performance.now();
  const result = routeOnGraph(region.index, req);
  const routeMs = performance.now() - r0;
  if (warmAheadEnabled) setTimeout(() => void warmRoutingAround(destination[0], destination[1]), 0);
  return done(result, { fetchMs, indexMs, routeMs, fetchedChunk, tiles: tiles.length, storeTiles });
}

if (typeof window !== "undefined") {
  (window as unknown as { __clearClientRoutingCache?: () => void }).__clearClientRoutingCache = clearClientRoutingCache;
}
